/**
 * Web-Audio SFX + looping Caribbean BGM bed + sand footsteps.
 * Starts muted; browsers keep AudioContext suspended until a user gesture —
 * unmute must resume() before anything is audible.
 */

const BGM_SRC = "/audio/tidecall-path.mp3";
const FOOTSTEPS_SRC = "/audio/footsteps-sand.mp3";

/** Soft pentatonic fallback if the BGM file cannot load. */
const FallbackNotes = [
  392, 440, 494, 587, 494, 440, 349, 440, 523, 440, 392, 330,
] as const;

const BGM_VOLUME = 0.22;
/** Quieter than BGM so sand steps sit under the bed. */
const FOOTSTEPS_VOLUME = 0.1;

export class TheaterAudio {
  private muted = true;
  private ctx: AudioContext | null = null;
  private bgm: HTMLAudioElement | null = null;
  private bgmFailed = false;
  private footsteps: HTMLAudioElement | null = null;
  private footstepsWanted = false;
  private musicGain: GainNode | null = null;
  private padOsc: OscillatorNode | null = null;
  private musicTimer: ReturnType<typeof setTimeout> | null = null;
  private musicStep = 0;
  /** Prevents overlapping unlock()/startMusic() from seeking to 0 mid-play. */
  private unlockInFlight: Promise<void> | null = null;

  get isMuted(): boolean {
    return this.muted;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (m) {
      this.stopMusic();
      this.pauseFootsteps();
      return;
    }
    void this.unlock();
  }

  /** Event blip — no-op while muted. */
  blip(freq: number): void {
    if (this.muted) return;
    void this.ensureCtx().then((ctx) => {
      if (this.muted || !ctx) return;
      this.playBlip(ctx, freq, 0.045, 0.12);
    });
  }

  /** Soft sand footsteps while a creature walks (e.g. boarding). */
  startFootsteps(): void {
    this.footstepsWanted = true;
    if (this.muted) return;
    void this.ensureCtx().then(() => {
      if (this.muted || !this.footstepsWanted) return;
      const el = this.ensureFootstepsElement();
      if (!el.paused && !el.ended) return;
      void el.play().catch(() => {
        /* autoplay / load race — next boarding retries */
      });
    });
  }

  stopFootsteps(): void {
    this.footstepsWanted = false;
    this.pauseFootsteps();
  }

  private pauseFootsteps(): void {
    if (!this.footsteps) return;
    this.footsteps.pause();
    try {
      this.footsteps.currentTime = 0;
    } catch {
      /* ignore seek races */
    }
  }

  private async unlock(): Promise<void> {
    if (this.unlockInFlight) return this.unlockInFlight;
    this.unlockInFlight = this.doUnlock().finally(() => {
      this.unlockInFlight = null;
    });
    return this.unlockInFlight;
  }

  private async doUnlock(): Promise<void> {
    const ctx = await this.ensureCtx();
    if (!ctx || this.muted) return;
    this.playBlip(ctx, 700, 0.06, 0.16);
    await this.startMusic(ctx);
    if (this.footstepsWanted) this.startFootsteps();
  }

  private async ensureCtx(): Promise<AudioContext | null> {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AC) return null;
    if (!this.ctx) this.ctx = new AC();
    if (this.ctx.state === "suspended") {
      try {
        await this.ctx.resume();
      } catch {
        /* gesture may still be required; next unmute retries */
      }
    }
    return this.ctx;
  }

  private playBlip(
    ctx: AudioContext,
    freq: number,
    peak: number,
    dur: number,
  ): void {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(peak, t + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private ensureBgmElement(): HTMLAudioElement {
    if (this.bgm) return this.bgm;

    const audio = new Audio();
    audio.loop = true;
    audio.preload = "auto";
    audio.volume = BGM_VOLUME;
    audio.src = BGM_SRC;

    audio.addEventListener(
      "error",
      () => {
        this.bgmFailed = true;
      },
      { once: true },
    );

    this.bgm = audio;
    return audio;
  }

  private ensureFootstepsElement(): HTMLAudioElement {
    if (this.footsteps) return this.footsteps;

    const audio = new Audio();
    audio.loop = true;
    audio.preload = "auto";
    audio.volume = FOOTSTEPS_VOLUME;
    audio.src = FOOTSTEPS_SRC;
    this.footsteps = audio;
    return audio;
  }

  private async startMusic(ctx: AudioContext): Promise<void> {
    const bgm = this.ensureBgmElement();

    if (!this.bgmFailed) {
      try {
        // Already playing — do not seek to 0 (that caused premature restarts).
        if (!bgm.paused && !bgm.ended) return;
        await bgm.play();
        return;
      } catch {
        /* fall through to WebAudio bed */
      }
    }

    this.startFallbackMusic(ctx);
  }

  private startFallbackMusic(ctx: AudioContext): void {
    if (this.musicGain) return;

    this.musicGain = ctx.createGain();
    this.musicGain.gain.value = 0.02;
    this.musicGain.connect(ctx.destination);

    this.padOsc = ctx.createOscillator();
    const padGain = ctx.createGain();
    this.padOsc.type = "sine";
    this.padOsc.frequency.value = 98;
    padGain.gain.value = 0.01;
    this.padOsc.connect(padGain);
    padGain.connect(this.musicGain);
    this.padOsc.start();

    this.musicStep = 0;
    const tick = () => {
      if (this.muted || !this.musicGain || !this.ctx) return;
      const c = this.ctx;
      const note = FallbackNotes[this.musicStep % FallbackNotes.length]!;
      const t = c.currentTime;
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = "triangle";
      osc.frequency.value = note;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.05, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.42);
      osc.connect(g);
      g.connect(this.musicGain);
      osc.start(t);
      osc.stop(t + 0.45);
      this.musicStep += 1;
      this.musicTimer = setTimeout(tick, 380);
    };
    tick();
  }

  private stopMusic(): void {
    if (this.bgm) {
      this.bgm.pause();
      try {
        this.bgm.currentTime = 0;
      } catch {
        /* ignore seek races while loading */
      }
    }
    if (this.musicTimer != null) {
      clearTimeout(this.musicTimer);
      this.musicTimer = null;
    }
    if (this.padOsc) {
      try {
        this.padOsc.stop();
      } catch {
        /* already stopped */
      }
      this.padOsc.disconnect();
      this.padOsc = null;
    }
    if (this.musicGain) {
      this.musicGain.disconnect();
      this.musicGain = null;
    }
  }
}
